import { useEffect, useState } from "react";
import { LogOut, Package, Truck, ChevronDown, ChevronRight, Camera, KeyRound, FileSpreadsheet } from "lucide-react";
import { PORTAL_API, portalAxios } from "./PortalApp";
import { PortalOrdersReport } from "./PortalOrdersReport";
import { PortalCustomerQuery } from "./PortalCustomerQuery";

const hdr = () => (sessionStorage.getItem("portal_token") ? { Authorization: `Bearer ${sessionStorage.getItem("portal_token")}` } : {});
const fmt = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString("en-IN"));
const STATUS = { open: "bg-sky-100 text-sky-800", partial: "bg-amber-100 text-amber-800", despatched: "bg-emerald-100 text-emerald-800", closed: "bg-zinc-200 text-zinc-700",
  packed: "bg-sky-100 text-sky-800", loading: "bg-amber-100 text-amber-800", shipped: "bg-emerald-100 text-emerald-800" };
const Badge = ({ s }) => <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold uppercase ${STATUS[s] || "bg-zinc-100"}`}>{s}</span>;

function Shipments() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState({});
  const [photos, setPhotos] = useState({});
  useEffect(() => { portalAxios.get(`${PORTAL_API}/shipments`, { headers: hdr() }).then((r) => setData(r.data)).catch(() => setData({ shipments: [] })); }, []);
  const loadPhotos = async (pl) => {
    if (photos[pl]) return;
    try { const r = await portalAxios.get(`${PORTAL_API}/shipments/${pl}/photos`, { headers: hdr() }); setPhotos({ ...photos, [pl]: r.data.photos || [] }); } catch { setPhotos({ ...photos, [pl]: [] }); }
  };
  if (!data) return <p className="text-sm text-zinc-500">Loading shipments…</p>;
  if (!data.shipments.length) return <p className="text-sm text-zinc-500" data-testid="portal-shipments-empty">No packing lists yet for your account.</p>;
  return (
    <div className="space-y-2" data-testid="portal-shipments">
      {data.shipments.map((s) => (
        <div key={s.pl_number} className="bg-white border rounded-xl overflow-hidden">
          <button className="w-full text-left px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-1" onClick={() => setOpen({ ...open, [s.pl_number]: !open[s.pl_number] })} data-testid={`portal-shipment-${s.pl_number}`}>
            {open[s.pl_number] ? <ChevronDown className="w-4 h-4 text-zinc-400" /> : <ChevronRight className="w-4 h-4 text-zinc-400" />}
            <span className="font-mono font-bold">{s.pl_number}</span>
            <Badge s={s.status} />
            <span className="text-xs text-zinc-500">{s.date} · {s.pallet_count} pallets · {fmt(s.total_boxes)} boxes · {fmt(s.total_pcs)} pcs · {fmt(Math.round(s.gross_kg || 0))} kg</span>
            <span className="text-xs text-zinc-600 ml-auto">Loaded {s.loaded_count}/{s.pallet_count}{s.container_no ? ` · Container ${s.container_no}` : ""}{s.invoice_no ? ` · Inv ${s.invoice_no}` : ""}</span>
          </button>
          {open[s.pl_number] && (
            <div className="border-t">
              <div className="px-4 py-2 text-xs text-zinc-500">PO: {s.po_numbers.join(", ") || "—"}</div>
              <table className="w-full text-xs">
                <thead className="bg-zinc-50 text-zinc-500"><tr><th className="text-left px-4 py-1">Pallet</th><th className="text-left px-2 py-1">ERP no</th><th className="text-left px-2 py-1">Articles</th><th className="text-right px-2 py-1">Boxes</th><th className="text-right px-2 py-1">Pcs</th><th className="text-right px-2 py-1">kg</th><th className="text-left px-2 py-1">Loaded</th></tr></thead>
                <tbody>
                  {s.pallets.map((p) => (
                    <tr key={p.pallet_no} className="border-t">
                      <td className="px-4 py-1 font-mono"><a className="text-emerald-700 underline" href={`/p/${p.pallet_no}`} target="_blank" rel="noreferrer">{p.pallet_no}</a></td>
                      <td className="px-2 py-1 font-mono">{p.erp_pallet_no || "—"}</td><td className="px-2 py-1 font-mono">{p.articles.join(", ")}</td>
                      <td className="px-2 py-1 text-right font-mono">{fmt(p.boxes)}</td><td className="px-2 py-1 text-right font-mono">{fmt(p.pcs)}</td><td className="px-2 py-1 text-right font-mono">{fmt(Math.round(p.gross_kg || 0))}</td>
                      <td className="px-2 py-1">{p.loaded ? <span className="text-emerald-700 font-bold">✓ loaded</span> : <span className="text-zinc-400">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {s.photos > 0 && (
                <div className="px-4 py-2 border-t">
                  <button className="text-xs text-sky-700 flex items-center gap-1" onClick={() => loadPhotos(s.pl_number)} data-testid={`portal-photos-${s.pl_number}`}><Camera className="w-3.5 h-3.5" /> {s.photos} container photo(s)</button>
                  {photos[s.pl_number] && <div className="flex flex-wrap gap-2 mt-2">{photos[s.pl_number].map((ph) => <img key={ph.id} src={ph.image} alt="container" className="h-28 rounded-md border" />)}</div>}
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export function PortalDashboard({ auth, onLogout, onSwitchCustomer, onChangePassword }) {
  const [tab, setTab] = useState("orders");
  const companies = auth.customers || [];
  const logout = async () => { try { await portalAxios.post(`${PORTAL_API}/auth/logout`, null, { headers: hdr() }); } catch { /* ignore */ } onLogout(); };
  return (
    <div className="min-h-screen bg-zinc-50" data-testid="portal-dashboard">
      <header className="bg-[#0F172A] text-white">
        <div className="max-w-[1500px] mx-auto px-4 py-4 flex items-center gap-3">
          <Package className="w-6 h-6 text-emerald-300" />
          <div className="mr-auto"><div className="text-[10px] uppercase tracking-[0.3em] text-emerald-300">Customer portal</div><div className="font-bold text-lg" data-testid="portal-customer-name">{auth.customer.name}</div></div>
          {companies.length > 1 && (
            <div className="flex rounded-full border border-white/20 overflow-hidden text-xs font-semibold" data-testid="portal-company-tabs">
              {companies.map((c) => (
                <button key={c.id} onClick={() => c.id !== auth.customer.id && onSwitchCustomer(c)}
                  className={`h-9 px-3 ${c.id === auth.customer.id ? "bg-emerald-500 text-white" : "text-white/80 hover:bg-white/10"}`}
                  data-testid={`portal-company-tab-${c.id}`}>{c.name}</button>
              ))}
            </div>
          )}
          <div className="text-right text-xs text-white/70 hidden sm:block">{auth.user?.name && auth.user.name !== auth.user.email ? <>{auth.user.name}<br /></> : null}{auth.user?.email}</div>
          {onChangePassword && <button onClick={onChangePassword} className="h-9 px-3 rounded-full border border-white/20 text-sm flex items-center gap-1 hover:bg-white/10" title="Change password" data-testid="portal-change-password-btn"><KeyRound className="w-4 h-4" /><span className="hidden sm:inline"> Password</span></button>}
          <button onClick={logout} className="h-9 px-3 rounded-full border border-white/20 text-sm flex items-center gap-1 hover:bg-white/10" data-testid="portal-logout"><LogOut className="w-4 h-4" /> Sign out</button>
        </div>
      </header>
      <main className="max-w-[1500px] mx-auto px-4 py-5 space-y-4">
        <div className="flex gap-2">
          {[["orders", "Orders — Despatch vs Pending", Package], ["query", "Order query (Excel)", FileSpreadsheet]].map(([k, l, I]) => (
            <button key={k} onClick={() => setTab(k)} className={`h-10 px-4 rounded-full text-sm font-semibold flex items-center gap-2 border ${tab === k ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-zinc-700"}`} data-testid={`portal-tab-${k}`}><I className="w-4 h-4" /> {l}</button>
          ))}
        </div>
        {tab === "query" ? <PortalCustomerQuery /> : <PortalOrdersReport />}
      </main>
    </div>
  );
}
